import { ReactNode, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Activity,
  Footprints,
  Banknote,
  FlaskConical,
  Beaker,
  Crown,
  BellRing,
  Server,
  MessageSquare,
  Settings,
  Coins,
  ShieldCheck,
  TestTube2,
  LifeBuoy,
  Repeat,
  ThumbsUp,
  LogOut,
  Sun,
  Moon,
  Gauge,
  Pencil,
  Wrench,
  Bug,
  Gift,
  Scale,
  Sparkles,
  Mail,
  Wallet,
  UsersRound,
  Gavel,
  Filter,
  Hammer,
  Send,
  SmilePlus,
  DollarSign,
  Target,
  Trophy,
  ListChecks,
  Ruler,
  Layers,
  ClipboardList,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { DateRangeFilter } from '@/components/DateRangeFilter';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  phase: number;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, phase: 0 },
  { to: '/financeiro', label: 'Financeiro', icon: TrendingUp, phase: 0 },
  { to: '/users', label: 'Users', icon: Users, phase: 1 },
  { to: '/cases', label: 'Cases', icon: Briefcase, phase: 1 },
  { to: '/realtime', label: 'Realtime', icon: Activity, phase: 2 },
  { to: '/journey', label: 'Journey', icon: Footprints, phase: 2 },
  { to: '/funil', label: 'Funil', icon: Filter, phase: 4 },
  { to: '/harness', label: 'JuanDz2', icon: Hammer, phase: 4 },
  { to: '/coletiva', label: 'Ação coletiva', icon: Users, phase: 4 },
  { to: '/monetization', label: 'Monetization', icon: Banknote, phase: 3 },
  { to: '/creditos', label: 'Créditos', icon: Wallet, phase: 4 },
  { to: '/top-clientes', label: '30 Clientes Top', icon: Trophy, phase: 4 },
  { to: '/testing', label: 'Testing', icon: FlaskConical, phase: 3 },
  { to: '/infra', label: 'Infra', icon: Server, phase: 2 },
  { to: '/rescue', label: 'Rescue', icon: LifeBuoy, phase: 2 },
  { to: '/chat', label: 'Chat', icon: MessageSquare, phase: 3 },
  { to: '/system', label: 'System', icon: Settings, phase: 2 },
  { to: '/ai-cost', label: 'AI Cost', icon: Coins, phase: 3 },
  { to: '/security', label: 'Security', icon: ShieldCheck, phase: 4 },
  { to: '/experiments', label: 'Experiments', icon: TestTube2, phase: 4 },
  { to: '/retencao', label: 'Retenção', icon: Repeat, phase: 4 },
  { to: '/atividade', label: 'Atividade', icon: UsersRound, phase: 4 },
  { to: '/satisfacao', label: 'Satisfação', icon: SmilePlus, phase: 4 },
  { to: '/impugnacoes', label: 'Impugnações', icon: Gavel, phase: 4 },
  { to: '/unit-economics', label: 'Unit Economics', icon: DollarSign, phase: 4 },
  { to: '/qualidade-va', label: 'Qualidade (VA)', icon: Target, phase: 4 },
  { to: '/lotes', label: 'Lotes', icon: Layers, phase: 4 },
  { to: '/va-por-verba', label: 'VA por verba', icon: ListChecks, phase: 4 },
  { to: '/golden5', label: 'Golden5 vs Perito', icon: Ruler, phase: 4 },
  { to: '/feedback', label: 'Feedback', icon: ThumbsUp, phase: 4 },
  { to: '/bayes', label: 'Bayes', icon: Gauge, phase: 4 },
  { to: '/pjc-ab', label: 'A/B .pjc', icon: Beaker, phase: 4 },
  { to: '/max-ab', label: 'A/B Max', icon: Crown, phase: 4 },
  { to: '/max-health', label: 'MAX Health', icon: ShieldCheck, phase: 4 },
  { to: '/ficha-ab', label: 'Ficha A/B', icon: ClipboardList, phase: 4 },
  { to: '/acompanhamento', label: 'Acompanhamento', icon: BellRing, phase: 4 },
  { to: '/juiz', label: 'Juiz', icon: Scale, phase: 4 },
  { to: '/concierge', label: 'Concierge', icon: Sparkles, phase: 4 },
  { to: '/edicao', label: 'Edição', icon: Pencil, phase: 4 },
  { to: '/melhoria', label: 'Melhoria', icon: Wrench, phase: 4 },
  { to: '/loop-erro', label: 'Loop de erro', icon: Bug, phase: 4 },
  { to: '/referrals', label: 'Referrals', icon: Gift, phase: 3 },
  { to: '/newsletter', label: 'Newsletter', icon: Mail, phase: 4 },
  { to: '/prospector', label: 'Prospector', icon: Send, phase: 4 },
];

interface LayoutProps {
  children: ReactNode;
}

// Routes that opt out of the global date filter (live or non-temporal views).
// /atividade, /satisfacao, /impugnacoes, /unit-economics e /qualidade-va têm
// seletor de período PRÓPRIO — o global confundiria.
const NO_DATE_FILTER_PATHS = [
  '/realtime',
  '/harness',
  '/atividade',
  '/satisfacao',
  '/impugnacoes',
  '/unit-economics',
  '/qualidade-va',
  '/va-por-verba',
  '/golden5',
  '/lotes',
  '/top-clientes',
  '/pjc-ab',
  '/max-ab',
  '/max-health',
  '/ficha-ab',
  '/acompanhamento',
  // [22/10] /prospector tem seletor de período PRÓPRIO (7/28/90/tudo, ligado
  // ao ?dias= dos endpoints). O date picker global aparecia aqui e não fazia
  // nada — o operador mexia no período e os números não mudavam.
  '/prospector',
  '/financeiro',
];

function pathUsesDateFilter(path: string): boolean {
  if (NO_DATE_FILTER_PATHS.some((p) => path === p || path.startsWith(p + '/'))) {
    return false;
  }
  // Detail routes (users/123, cases/456) handle their own scoping.
  if (/^\/users\/[^/]+/.test(path)) return false;
  if (/^\/cases\/[^/]+/.test(path)) return false;
  return true;
}

export function Layout({ children }: LayoutProps) {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { user, logout } = useAuth();
  const [dark, setDark] = useState<boolean>(() =>
    document.documentElement.classList.contains('dark'),
  );
  const showDateFilter = pathUsesDateFilter(currentPath);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    if (next) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }

  return (
    <div className="flex h-full w-full bg-slate-950 text-slate-100">
      <aside className="flex w-60 shrink-0 flex-col border-r border-slate-800 bg-slate-900/50">
        <div className="border-b border-slate-800 px-5 py-4">
          <h1 className="text-lg font-semibold tracking-tight">
            JusQuant <span className="text-blue-400">Admin</span>
          </h1>
          <p className="text-xs text-slate-400">Phase 1 — Users + Cases</p>
        </div>

        <nav className="flex-1 space-y-1 px-2 py-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active =
              currentPath === item.to ||
              (item.to !== '/' && currentPath.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition ${
                  active
                    ? 'bg-blue-600/20 text-blue-300'
                    : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.phase > 0 && (
                  <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                    P{item.phase}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 px-3 py-3 text-xs text-slate-500">
          v0.1.0 — admin.jusquant.ai
        </div>
      </aside>

      <main className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900/40 px-6 py-3">
          <div className="text-sm text-slate-400">
            {NAV_ITEMS.find(
              (i) =>
                i.to === currentPath ||
                (i.to !== '/' && currentPath.startsWith(i.to)),
            )?.label ?? 'Admin'}
          </div>
          <div className="flex items-center gap-3">
            {showDateFilter && <DateRangeFilter />}
            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-md border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800"
              aria-label="Toggle theme"
            >
              {dark ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <div className="text-sm text-slate-300">
              {user?.email ?? 'anonymous'}
            </div>
            <button
              type="button"
              onClick={() => {
                void logout();
              }}
              className="inline-flex items-center gap-1 rounded-md bg-red-600/20 px-3 py-1.5 text-sm text-red-300 hover:bg-red-600/30"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">{children}</div>
      </main>
    </div>
  );
}
